import './radio-group-checkbox.scss';

import React, { PropsWithChildren, useEffect } from 'react';
import { useRadio, useRadioGroup } from 'react-aria';
import { type RadioGroupState, useRadioGroupState } from 'react-stately';

import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';

import { cn } from '../../utils/helpers';

const RadioContext = React.createContext<RadioGroupState | null>(null);

type RadioGroupCheckboxProps<TValue> = PropsWithChildren & {
  errorMessage?: string;
  label: string;
  defaultValue: TValue;
  onChange: (selectedValue: TValue) => void;
};

// Looks like VSCode has some issues with an arrow function here without adding some verbose type assertions.
// eslint-disable-next-line react/function-component-definition, prefer-arrow-functions/prefer-arrow-functions
export function RadioGroupCheckbox<TValue extends string = string>(
  props: RadioGroupCheckboxProps<TValue>,
) {
  const { onChange, ...rest } = props;
  const { children, errorMessage, label } = rest;
  const state = useRadioGroupState(rest);
  const { radioGroupProps, labelProps, errorMessageProps } = useRadioGroup(
    rest,
    state,
  );

  const { selectedValue } = state;
  useEffect(() => {
    onChange(selectedValue as TValue);
  }, [selectedValue]);

  return (
    <div {...radioGroupProps} className="RadioGroupCheckbox">
      <p className="RadioGroupCheckbox__label" {...labelProps}>
        {label}
      </p>
      <RadioContext.Provider value={state}>
        <div className="RadioGroupCheckbox__list gap-comfy-xs my-comfy-lg">
          {children}
        </div>
      </RadioContext.Provider>
      {errorMessage && state.isInvalid && (
        <div {...errorMessageProps} style={{ color: 'red', fontSize: 12 }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
}

export const RadioGroupCheckboxItem = (
  props: PropsWithChildren<{ value: string }>,
) => {
  const { children } = props;
  const state = React.useContext(RadioContext) as RadioGroupState;
  const ref = React.useRef(null);
  const { inputProps, isSelected, labelProps } = useRadio(props, state, ref);

  return (
    <label
      {...labelProps}
      className={cn([
        'RadioGroupCheckbox__item flex justify-between',
        {
          'RadioGroupCheckbox__item--checked': isSelected,
          'RadioGroupCheckbox__item--unchecked': !isSelected,
        },
      ])}
      htmlFor={inputProps.name}
    >
      <input
        {...inputProps}
        ref={ref}
        id={inputProps.name}
        className="sr-only"
      />
      {children}
      <span className="RadioGroupCheckbox__check">
        <ArkIcon
          iconName="placeholder"
          className="checkmark-circle"
          size="1.5rem"
          color={cn({
            'var(--color-input-border-neutral-mild)': !isSelected,
            'var(--color-action-background-subtle)': isSelected,
          })}
        />
        {isSelected && <ArkIcon iconName="check" className="checkmark" />}
      </span>
    </label>
  );
};
